<template>
  <section class="terms-content">
    <header class="terms-header">
      <p class="terms-eyebrow">WEB APP END USER LICENSE AGREEMENT</p>
      <h1>Terms and Conditions</h1>
      <p class="terms-intro">
        PLEASE READ THIS AGREEMENT CAREFULLY BEFORE USING THIS WEB APPLICATION. BY
        ACCESSING OR USING THIS WEB APPLICATION ("THE WEB APP"), YOU AGREE TO BE BOUND
        BY THE TERMS OF THIS END USER LICENSE AGREEMENT ("EULA"). IF YOU DO NOT AGREE
        WITH THE TERMS OF THIS EULA, DO NOT USE OR ACCESS THE WEB APP.
      </p>
    </header>

    <article v-for="section in sections" :key="section.title" class="terms-section">
      <h2>{{ section.title }}</h2>
      <p v-for="paragraph in section.paragraphs" :key="paragraph">{{ paragraph }}</p>
      <ul v-if="section.bullets?.length" class="terms-list">
        <li v-for="bullet in section.bullets" :key="bullet">{{ bullet }}</li>
      </ul>
    </article>

    <footer class="terms-footer">
      <p>
        By clicking "Accept" or using the Web App, you acknowledge that you have read,
        understood, and agree to be bound by this EULA.
      </p>
    </footer>
  </section>
</template>

<script setup lang="ts">
const sections = [
  {
    title: '1. License Grant',
    paragraphs: [
      "The Web App is licensed, not sold. The Licensor (INNER REALITY LTD, registered in England and Wales with Company Number 13434508) grants you a personal, limited, non-exclusive, non-transferable, and revocable license to access and use the Web App solely for marketing products offered by the Licensor's Customer, in accordance with the terms of this EULA.",
      "The Licensor's Customer (also called the Brand) is a company or companies whose products are being marketed on the Web App.",
    ],
  },
  {
    title: '2. Account Registration',
    paragraphs: [
      'To access certain features of the Web App, you may be required to register an account. You agree to:',
    ],
    bullets: [
      'Provide accurate, current, and complete information during the registration process.',
      'Maintain the confidentiality of your login credentials and restrict access to your account.',
      'Be responsible for all activities that occur under your account.',
    ],
  },
  {
    title: '3. Data Privacy Policy',
    paragraphs: [
      "The Licensor is keen to strike a fair balance between your personal privacy and ensuring that you obtain full value from the products and services it offers you, while ensuring that it complies with all protection the Data Protection Act 2018 ('the Act') and the General Data Protection Regulation ('GDPR') affords to you. Any personal data that you provide will be treated with the utmost care and security and will not be shared with other third parties.",
      "The Licensor and/or the Licensor's Customer will hold your personal information on their systems for as long as it is still used for the purpose of marketing their products and services. Your data will not be shared with other third parties.",
      'By using the Web App, you agree to the terms of the Data Privacy Policy.',
    ],
  },
  {
    title: '4. Restrictions on Use',
    paragraphs: [
      'You agree not to:',
    ],
    bullets: [
      'Copy, modify, distribute, or create derivative works based on the Web App or its content.',
      'Reverse engineer, decompile, or otherwise attempt to extract the source code of the Web App.',
      'Use the Web App for any illegal purpose or in violation of applicable laws.',
      "Use the Web App in a manner that could disable, overburden, or impair its functioning, or interfere with any other party's use.",
      'Share your account or login credentials with others or allow others to access your account.',
    ],
  },
  {
    title: '5. Intellectual Property',
    paragraphs: [
      'All content, design, software, and materials associated with the Web App are the intellectual property of the Licensor and are protected by applicable copyright, trademark, and other intellectual property laws. You are not granted any ownership rights in the Web App or its content.',
    ],
  },
  {
    title: '6. Termination',
    paragraphs: [
      'This EULA is effective until terminated. The Licensor may terminate or suspend your access to the Web App at any time without notice for breach of this EULA. Upon termination, your right to use the Web App will immediately cease. All provisions of this EULA which by their nature should survive termination shall survive, including but not limited to ownership provisions, warranty disclaimers, and limitations of liability.',
    ],
  },
  {
    title: '7. Warranty Disclaimer',
    paragraphs: [
      'The Web App is provided "AS IS" and "AS AVAILABLE," without any warranties of any kind, either express or implied. To the fullest extent permitted by law, the Licensor disclaims all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, non-infringement, and availability. The Licensor does not warrant that the Web App will be uninterrupted or error-free, or that any defects will be corrected.',
    ],
  },
  {
    title: '8. Limitation of Liability',
    paragraphs: [
      'To the maximum extent permitted by law, in no event will the Licensor or any of their partners or customers be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, use, or profits, arising from or related to your use or inability to use the Web App, even if the Licensor has been advised of the possibility of such damages.',
    ],
  },
  {
    title: '9. Governing Law',
    paragraphs: [
      'This EULA is governed by and construed in accordance with the laws of England, without regard to its conflict of law principles. Any disputes arising under this EULA shall be subject to the exclusive jurisdiction of the courts in England.',
    ],
  },
  {
    title: '10. Amendments',
    paragraphs: [
      'The Licensor reserves the right to update or modify this EULA at any time. If changes are made, the updated EULA will be posted on the Web App, and continued use of the Web App following such changes constitutes your acceptance of the updated terms.',
    ],
  },
  {
    title: '11. Entire Agreement',
    paragraphs: [
      'This EULA, together with the Privacy Policy, constitutes the entire agreement between you and the Licensor regarding the use of the Web App, superseding any prior agreements.',
    ],
  },
]
</script>

<style scoped>
.terms-content {
  width: min(960px, 100%);
  margin: 0 auto;
  padding: clamp(2.5rem, 6vw, 4.5rem) clamp(1.5rem, 4vw, 3rem) clamp(1.5rem, 4vw, 3rem);
  color: var(--color-background);
}

.terms-header,
.terms-section,
.terms-footer {
  background: var(--ui-inverse-surface);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 20px;
  box-shadow: 0 18px 38px rgba(8, 17, 28, 0.2);
  padding: clamp(1.25rem, 3vw, 2rem);
  text-align: left;
}

.terms-header {
  margin-bottom: 1rem;
}

.terms-eyebrow {
  margin: 0 0 0.75rem;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.78);
}

h1 {
  margin: 0 0 1rem;
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1.05;
  color: #ffffff;
}

.terms-intro {
  margin: 0;
  font-size: 1rem;
  line-height: 1.65;
  color: #ffffff;
}

.terms-section {
  margin-top: 1rem;
}

h2 {
  margin: 0 0 0.85rem;
  font-size: 1.25rem;
  line-height: 1.2;
  color: #ffffff;
}

p {
  margin: 0 0 0.85rem;
  line-height: 1.7;
  color: #ffffff;
}

p:last-child {
  margin-bottom: 0;
}

.terms-list {
  margin: 0.25rem 0 0;
  padding-left: 1.25rem;
}

.terms-list li {
  margin-bottom: 0.65rem;
  line-height: 1.65;
  color: #ffffff;
}

.terms-footer {
  margin-top: 1rem;
  font-weight: 700;
}
</style>
